import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContactModal from '../components/ContactModal';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SpeakingPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    document.title = 'Speaking | Kevyn Franco';
  }, []);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box>
      <Header />
      
      {/* Hero Section with Video Background */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#2a2a2a',
          color: 'white',
          py: { xs: 8, md: 12 },
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Background overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: '800px' }}>
            {/* Title with Red Backgrounds */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-block',
                  backgroundColor: '#dc143c',
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 0.5, md: 1 },
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Impact", "Arial Black", sans-serif',
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    lineHeight: 1,
                    fontWeight: 900,
                    color: '#1a1a1a',
                  }}
                >
                  Practical,
                </Typography>
              </Box>
              <br />
              <Box
                sx={{
                  display: 'inline-block',
                  backgroundColor: '#dc143c',
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 0.5, md: 1 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Impact", "Arial Black", sans-serif',
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    lineHeight: 1,
                    fontWeight: 900,
                    color: '#1a1a1a',
                  }}
                >
                  Insightful, and Fun.
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                fontSize: { xs: '1.125rem', md: '1.25rem' },
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              That's how I want my talks to be remembered. Your audience should
              come away with actionable advice, not just sit through self-promotion.
              My talks are structured to be valuable and engaging.
            </Typography>

            {/* CTA Button */}
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={handleOpenModal}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                '&:hover': {
                  borderColor: '#dc143c',
                  backgroundColor: '#dc143c',
                },
              }}
            >
              Invite me to Speak
            </Button>
          </Box>
        </Container>
      </Box>

      {/* As Featured In Section */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: '#999',
              mb: 4,
              fontSize: { xs: '0.875rem', md: '1rem' },
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            As Featured In
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
              opacity: 0.4,
            }}
          >
            {['TechCrunch', 'Forbes', 'Wired', 'VentureBeat', 'The Next Web'].map((publication) => (
              <Typography
                key={publication}
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#666',
                  fontFamily: 'Arial, sans-serif',
                }}
              >
                {publication}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Looking for a Speaker Section */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 4,
              textTransform: 'uppercase',
            }}
          >
            LOOKING FOR A SPEAKER FOR YOUR NEXT CONFERENCE OR VIRTUAL EVENT?
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              lineHeight: 1.8,
              mb: 3,
              maxWidth: '900px',
            }}
          >
            With over 8 years of experience building scalable digital products, I provide
            strategic insights and practical advice that helps audiences of software
            professionals turn advanced technical concepts into real results
            for their projects.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              lineHeight: 1.8,
              mb: 4,
              maxWidth: '900px',
            }}
          >
            Great conference speakers leave audiences seeing their work in a new
            way and feeling excited to apply the concepts. Using expertise,
            professionalism, and humor, your audience will remember my conference
            or keynote address for both its engaging delivery and practical
            application.
          </Typography>

          <Button
            onClick={handleOpenModal}
            sx={{
              color: '#1a1a1a',
              fontWeight: 700,
              fontSize: '1.125rem',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              textDecorationThickness: '2px',
              p: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#dc143c',
              },
            }}
          >
            Contact Me
          </Button>
        </Container>
      </Box>

      {/* Speaking Topics Section */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
            }}
          >
            SPEAKING TOPICS
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 4,
            }}
          >
            {[
              {
                title: 'AI-Powered Products',
                description: 'Building scalable products with LLMs, NLP, and intelligent systems',
              },
              {
                title: 'Full-Stack Development',
                description: 'Modern Python and JavaScript architectures for enterprise applications',
              },
              {
                title: 'Cloud Infrastructure',
                description: 'Designing and deploying resilient systems on AWS, GCP, and Azure',
              },
              {
                title: 'Product Engineering',
                description: 'From concept to production: building products that scale',
              },
            ].map((topic, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: 'white',
                  p: 4,
                  borderLeft: '4px solid #dc143c',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 2,
                  }}
                >
                  {topic.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#666',
                  }}
                >
                  {topic.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Past Speaking Events 2025 */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
            }}
          >
            SPEAKING EVENTS 2025
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 4,
            }}
          >
            {[
              {
                event: 'AI Summit Latin America',
                location: 'Lima, Peru',
                date: 'March 2025',
                topic: 'Building Production-Ready AI Systems',
              },
              {
                event: 'DevOps Days LATAM',
                location: 'São Paulo, Brazil',
                date: 'May 2025',
                topic: 'Scaling Microservices with Kubernetes',
              },
              {
                event: 'PyCon Latin America',
                location: 'Buenos Aires, Argentina',
                date: 'July 2025',
                topic: 'Python for AI-Powered Applications',
              },
              {
                event: 'Tech Summit Mexico',
                location: 'Mexico City, Mexico',
                date: 'September 2025',
                topic: 'Cloud-Native Architecture Patterns',
              },
            ].map((event, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 4,
                  borderTop: '3px solid #dc143c',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 1,
                  }}
                >
                  {event.event}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    color: '#666',
                    mb: 0.5,
                  }}
                >
                  {event.location} • {event.date}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#333',
                    fontStyle: 'italic',
                  }}
                >
                  "{event.topic}"
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Want to Learn More Section */}
      <Box
        sx={{
          backgroundColor: '#1a1a1a',
          color: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: '900px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 900,
                fontFamily: '"Impact", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
              }}
            >
              Want to Learn More About the Tech World?
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.125rem', md: '1.25rem' },
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              At my mentorship program, you'll work with guidance that values your growth and expertise. 
              My philosophy is simple – help driven individuals who foster a culture of continuous learning 
              and innovation in the tech industry.
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.125rem', md: '1.25rem' },
                lineHeight: 1.8,
                mb: 6,
              }}
            >
              Whether you're starting your journey in software engineering, looking to specialize in AI, 
              or want to level up your cloud infrastructure skills, I'm here to help you navigate the 
              ever-evolving tech landscape.
            </Typography>

            <Button
              variant="contained"
              onClick={handleOpenModal}
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: '#dc143c',
                color: 'white',
                px: 5,
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 700,
                '&:hover': {
                  backgroundColor: '#b01030',
                },
              }}
            >
              Get in Touch
            </Button>
          </Box>
        </Container>
      </Box>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal open={modalOpen} onClose={handleCloseModal} type="speaking" />
    </Box>
  );
};

export default SpeakingPage;